import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { AppFeatureExtensions } from '@app/app-feature-extensions.config';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';

import { loadAssociationsForCandidateOnAssignToJobComponent } from '@collections/associations/associations.actions';
import {
  associationStatusConfigurationsLoad,
  getAssociationStatusConfigurationsForJobsReady,
} from '@modules/assigns/store';
import { getCandidateAssociatedJobsIds } from '@modules/assigns/store/assigns.selectors';
import { AssignToJobService } from '@shared/assign-to-job/assign-to-job.service';
import { JobTag } from '@shared/models';
import { JobStatusEnum } from '@shared/models/job-statuses';
import { JobService } from '@shared/services';
import { isTruthy } from '@shared/utils';
import { isModalConfirmed, ModalShow } from '@shared/modal';

@Component({
  selector: 'mojo-assign-to-job',
  templateUrl: './assign-to-job.component.html',
  styleUrls: ['./assign-to-job.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AssignToJobComponent implements OnDestroy {
  jobStatusEnum = JobStatusEnum;
  sub = new Subscription();

  private _candidateId: number;
  get candidateId(): number {
    return this._candidateId;
  }
  @Input() set candidateId(candidateId: number) {
    this._candidateId = candidateId;
    if (candidateId) {
      this.store.dispatch(loadAssociationsForCandidateOnAssignToJobComponent({ candidateId }));
      this.tagsSelected$ = this.store
        .select(getCandidateAssociatedJobsIds, { candidateId })
        .pipe(filter(isTruthy), take(1));
    }
  }

  @Input() form: UntypedFormGroup;
  @Input() allowIgnoreAssigning = false;
  @Output() assigned = new EventEmitter<string[]>();

  jobs: JobTag[] = [];
  saveClicked: boolean;
  tagsSelected$: Observable<number[]>;

  associationConfigurationsLoaded$ = this.store.select(
    getAssociationStatusConfigurationsForJobsReady,
    { jobIds: this.jobs.map((job) => job.id) },
  );

  greenhouseFE = AppFeatureExtensions.GREENHOUSE_ATS;

  constructor(
    public jobService: JobService,
    private store: Store<{}>,
    private associationService: AssignToJobService,
  ) {}

  private associateCandidateToSelectedJobs(): void {
    this.associationService.addMultipleAssociation(
      this.jobs.map((job) => job.id),
      this.candidateId,
      this.form.controls.noteComment.value,
    );
  }

  jobSelectionChange(jobs: JobTag[]) {
    jobs.forEach((job) =>
      this.store.dispatch(associationStatusConfigurationsLoad({ jobId: job.id })),
    );
  }

  onSaveClicked(): void {
    this.saveClicked = true;
    this.associateCandidateToSelectedJobs();
    this.assigned.emit(this.jobs.map((job) => job.label));
  }

  closeDialog() {
    this.assigned.emit([]);
    this.sub.add(this.store.select(isModalConfirmed).subscribe());
  }

  ngOnDestroy() {
    this.sub.unsubscribe();
  }
}
